import { useEffect, useState } from "react";
import { X as XIcon, Plus, Tag, Calendar, Percent, DollarSign, Package, Check } from "lucide-react";
import { Button } from "../../components/UI/Button";
import { CompanyAdminLayout } from "../../components/CompanyAdmin/CompanyAdminLayout";
import Table from "../../components/UI/Table";
import { useParams } from "react-router-dom";
import { companyPricingUseCase, companyProductUseCase } from "di/container";
import type {
  EventDiscount,
  EventDiscountRequest,
  DiscountType,
} from "domain/types/pricing";
import type { ProductDto } from "domain/types/company/company-product";

const EventDiscountManagementPage: React.FC = () => {
  const { companyId } = useParams();
  const cid = Number(companyId);

  const [eventDiscounts, setEventDiscounts] = useState<EventDiscount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState<EventDiscount | null>(
    null,
  );
  const [formData, setFormData] = useState<EventDiscountRequest>({
    name: "",
    discountType: "PERCENTAGE",
    discountValue: 10,
    startDate: "",
    endDate: "",
    productIds: null,
    categoryIds: null,
    isActive: true,
  });

  // 상품 목록 및 선택 상태
  const [products, setProducts] = useState<ProductDto[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [applyToAll, setApplyToAll] = useState(true);
  const [selectedProductIds, setSelectedProductIds] = useState<number[]>([]);

  const fetchEventDiscounts = async () => {
    if (!Number.isFinite(cid)) return;
    try {
      setIsLoading(true);
      const result = await companyPricingUseCase.getEventDiscounts(cid);
      setEventDiscounts(result);
    } catch (e) {
      console.error(e);
      alert("이벤트 할인 목록을 불러오지 못했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProducts = async () => {
    if (!Number.isFinite(cid)) return;
    try {
      setIsLoadingProducts(true);
      const result = await companyProductUseCase.getProducts(cid);
      // ProductManagementDto 또는 ProductDto[] 형태로 반환될 수 있음
      if (Array.isArray(result)) {
        setProducts(result);
      } else if (result && 'products' in result) {
        setProducts((result as { products: ProductDto[] }).products);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  useEffect(() => {
    fetchEventDiscounts();
    fetchProducts();
  }, [companyId]);

  const resetFormData = () => {
    setFormData({
      name: "",
      discountType: "PERCENTAGE",
      discountValue: 10,
      startDate: "",
      endDate: "",
      productIds: null,
      categoryIds: null,
      isActive: true,
    });
    setEditingDiscount(null);
    setApplyToAll(true);
    setSelectedProductIds([]);
  };

  const openCreateModal = () => {
    resetFormData();
    // 기본 날짜 설정 (오늘부터 한 달 후까지)
    const today = new Date();
    const nextMonth = new Date(today);
    nextMonth.setMonth(nextMonth.getMonth() + 1);

    setFormData({
      ...formData,
      startDate: today.toISOString().slice(0, 16),
      endDate: nextMonth.toISOString().slice(0, 16),
    });
    setIsModalOpen(true);
  };

  const openEditModal = (discount: EventDiscount) => {
    setEditingDiscount(discount);
    setFormData({
      name: discount.name,
      discountType: discount.discountType,
      discountValue: discount.discountValue,
      startDate: discount.startDate.slice(0, 16),
      endDate: discount.endDate.slice(0, 16),
      productIds: discount.productIds,
      categoryIds: discount.categoryIds,
      isActive: discount.isActive,
    });
    // 상품 선택 상태 복원
    if (discount.productIds && discount.productIds.length > 0) {
      setApplyToAll(false);
      setSelectedProductIds(discount.productIds);
    } else {
      setApplyToAll(true);
      setSelectedProductIds([]);
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      alert("이벤트 이름을 입력해주세요.");
      return;
    }
    if (!formData.startDate || !formData.endDate) {
      alert("이벤트 기간을 설정해주세요.");
      return;
    }
    if (new Date(formData.startDate) >= new Date(formData.endDate)) {
      alert("종료일은 시작일보다 이후여야 합니다.");
      return;
    }

    try {
      const requestData = {
        ...formData,
        startDate: new Date(formData.startDate).toISOString(),
        endDate: new Date(formData.endDate).toISOString(),
        productIds: applyToAll ? null : (selectedProductIds.length > 0 ? selectedProductIds : null),
      };

      if (editingDiscount) {
        await companyPricingUseCase.updateEventDiscount(
          cid,
          editingDiscount.id,
          requestData,
        );
      } else {
        await companyPricingUseCase.createEventDiscount(cid, requestData);
      }

      await fetchEventDiscounts();
      setIsModalOpen(false);
      resetFormData();
    } catch (e) {
      console.error(e);
      alert(
        editingDiscount
          ? "이벤트 할인 수정에 실패했습니다."
          : "이벤트 할인 추가에 실패했습니다.",
      );
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("정말 삭제하시겠습니까?")) return;
    try {
      await companyPricingUseCase.deleteEventDiscount(cid, id);
      await fetchEventDiscounts();
    } catch (e) {
      console.error(e);
      alert("삭제에 실패했습니다.");
    }
  };

  const handleToggleActive = async (discount: EventDiscount) => {
    try {
      await companyPricingUseCase.updateEventDiscount(cid, discount.id, {
        name: discount.name,
        discountType: discount.discountType,
        discountValue: discount.discountValue,
        startDate: discount.startDate,
        endDate: discount.endDate,
        productIds: discount.productIds,
        categoryIds: discount.categoryIds,
        isActive: !discount.isActive,
      });
      await fetchEventDiscounts();
    } catch (e) {
      console.error(e);
      alert("상태 변경에 실패했습니다.");
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const formatDiscountValue = (type: DiscountType, value: number) => {
    if (type === "PERCENTAGE") {
      return `${value}%`;
    }
    return `${value.toLocaleString()}원`;
  };

  const getDiscountStatus = (discount: EventDiscount) => {
    if (!discount.isActive) {
      return { label: "비활성", color: "bg-gray-100 text-gray-600" };
    }
    const now = new Date();
    const start = new Date(discount.startDate);
    const end = new Date(discount.endDate);

    if (now < start) {
      return { label: "예정", color: "bg-blue-100 text-blue-600" };
    }
    if (now > end) {
      return { label: "종료", color: "bg-gray-100 text-gray-600" };
    }
    return { label: "진행중", color: "bg-green-100 text-green-600" };
  };

  const toggleProductSelection = (productId: number) => {
    setSelectedProductIds((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  };

  const getApplyTargetLabel = (discount: EventDiscount) => {
    if (!discount.productIds || discount.productIds.length === 0) {
      return "전체 상품";
    }
    return `${discount.productIds.length}개 상품`;
  };

  return (
    <CompanyAdminLayout>
      <div className="p-5">
        <div className="mb-[28px] flex flex-col items-start gap-[0.5rem]">
          <h2 className="text-[1.375rem] font-semibold">이벤트 할인 관리</h2>
          <p className="text-sm text-gray-500">
            기간 한정 할인 이벤트를 생성하고 관리합니다.
          </p>
        </div>

        <div className="mb-[1.25rem] p-[1.25rem] border border-[#d9d9d9] rounded-[0.625rem] bg-white flex flex-col md:flex-row gap-[0.75rem] justify-between items-center">
          <div className="flex gap-4 text-sm">
            <span className="text-gray-600">
              전체:{" "}
              <span className="font-semibold">{eventDiscounts.length}</span>개
            </span>
            <span className="text-green-600">
              진행중:{" "}
              <span className="font-semibold">
                {
                  eventDiscounts.filter(
                    (d) => getDiscountStatus(d).label === "진행중",
                  ).length
                }
              </span>
              개
            </span>
            <span className="text-blue-600">
              예정:{" "}
              <span className="font-semibold">
                {
                  eventDiscounts.filter(
                    (d) => getDiscountStatus(d).label === "예정",
                  ).length
                }
              </span>
              개
            </span>
          </div>
          <Button
            onClick={openCreateModal}
            className="h-[2.5rem] px-4 bg-[#4a6cf7] text-white flex items-center gap-2"
          >
            <Plus size={16} />
            이벤트 추가
          </Button>
        </div>

        {isLoading ? (
          <div className="text-center py-10 text-gray-500">로딩 중...</div>
        ) : eventDiscounts.length === 0 ? (
          <div className="text-center py-10 bg-white rounded-lg border border-gray-200">
            <Tag className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">등록된 이벤트 할인이 없습니다.</p>
            <Button
              onClick={openCreateModal}
              className="bg-[#4a6cf7] text-white"
            >
              첫 이벤트 만들기
            </Button>
          </div>
        ) : (
          <div className="text-[12px]">
            <Table
              columnWidths={[
                "160px",
                "90px",
                "90px",
                "100px",
                "120px",
                "120px",
                "70px",
                "70px",
                "100px",
              ]}
              tabledata={{
                head: [
                  <>이벤트명</>,
                  <>할인 유형</>,
                  <>할인 값</>,
                  <>적용 대상</>,
                  <>시작일</>,
                  <>종료일</>,
                  <>상태</>,
                  <>활성화</>,
                  <>작업</>,
                ],
                body: eventDiscounts.map((discount) => {
                  const status = getDiscountStatus(discount);
                  return [
                    <div className="font-medium">{discount.name}</div>,
                    <div className="flex items-center gap-1">
                      {discount.discountType === "PERCENTAGE" ? (
                        <Percent size={14} className="text-blue-500" />
                      ) : (
                        <DollarSign size={14} className="text-green-500" />
                      )}
                      {discount.discountType === "PERCENTAGE"
                        ? "정률"
                        : "정액"}
                    </div>,
                    <>
                      {formatDiscountValue(
                        discount.discountType,
                        discount.discountValue,
                      )}
                    </>,
                    <div className="flex items-center gap-1">
                      <Package size={14} className="text-gray-500" />
                      <span>{getApplyTargetLabel(discount)}</span>
                    </div>,
                    <>{formatDate(discount.startDate)}</>,
                    <>{formatDate(discount.endDate)}</>,
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs ${status.color}`}
                    >
                      {status.label}
                    </span>,
                    <button
                      onClick={() => handleToggleActive(discount)}
                      className={`w-9 h-5 rounded-full transition-colors ${
                        discount.isActive ? "bg-[#4285F4]" : "bg-[#d0d0d0]"
                      } relative flex items-center`}
                    >
                      <span
                        className={`w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${
                          discount.isActive
                            ? "translate-x-[18px]"
                            : "translate-x-[2px]"
                        }`}
                      />
                    </button>,
                    <div className="flex gap-1.5">
                      <button
                        className="px-2.5 py-1 text-[11px] text-[#555] bg-white border border-[#d0d0d0] rounded-[4px] hover:bg-[#f5f5f5] hover:border-[#bbb] transition-colors"
                        onClick={() => openEditModal(discount)}
                      >
                        수정
                      </button>
                      <button
                        className="px-2.5 py-1 text-[11px] text-[#555] bg-white border border-[#d0d0d0] rounded-[4px] hover:bg-[#f5f5f5] hover:border-[#bbb] transition-colors"
                        onClick={() => handleDelete(discount.id)}
                      >
                        삭제
                      </button>
                    </div>,
                  ];
                }),
              }}
            />
          </div>
        )}

        {/* 이벤트 추가/수정 모달 */}
        {isModalOpen && (
          <div className="fixed inset-0 z-[5000] flex items-center justify-center bg-black/40">
            <div className="w-[500px] rounded-[10px] bg-white p-[20px] shadow-lg">
              {/* 헤더 */}
              <div className="mb-[24px] flex items-center justify-between">
                <h3 className="text-[16px] font-semibold text-[#252525] text-left">
                  {editingDiscount ? "이벤트 수정" : "이벤트 추가"}
                </h3>
                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    resetFormData();
                  }}
                  className="text-[#787A7D] hover:text-[#555]"
                >
                  <XIcon size={20} />
                </button>
              </div>

              <div className="flex flex-col gap-[16px]">
                {/* 이벤트명 */}
                <div className="flex flex-col gap-[6px]">
                  <label className="text-[14px] text-black text-left">
                    이벤트명 *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full h-[36px] rounded-[4px] border border-[#d9d9d9] px-[12px] text-[13px] text-[#252525] text-left focus:border-[#4285F4] focus:outline-none"
                    placeholder="예: 봄맞이 10% 할인 이벤트"
                  />
                </div>

                {/* 할인 유형 */}
                <div className="flex flex-col gap-[6px]">
                  <label className="text-[14px] text-black text-left">
                    할인 유형 *
                  </label>
                  <div className="grid grid-cols-2 gap-[8px]">
                    {/* %로 할인 카드 */}
                    <div
                      onClick={() =>
                        setFormData({
                          ...formData,
                          discountType: "PERCENTAGE",
                        })
                      }
                      className={`h-[36px] flex items-center justify-between px-[12px] rounded-[4px] border cursor-pointer transition-all ${
                        formData.discountType === "PERCENTAGE"
                          ? "border-[#4285F4] bg-[#F0F6FF]"
                          : "border-[#d9d9d9] hover:border-[#bbb] bg-white"
                      }`}
                    >
                      <span className={`text-[13px] ${formData.discountType === "PERCENTAGE" ? "text-[#4285F4] font-medium" : "text-[#252525]"}`}>
                        %로 할인
                      </span>
                      {formData.discountType === "PERCENTAGE" && (
                        <div className="w-[18px] h-[18px] rounded-full bg-[#4285F4] flex items-center justify-center">
                          <Check size={11} className="text-white" />
                        </div>
                      )}
                    </div>
                    {/* 금액 할인 카드 */}
                    <div
                      onClick={() =>
                        setFormData({
                          ...formData,
                          discountType: "FIXED_AMOUNT",
                        })
                      }
                      className={`h-[36px] flex items-center justify-between px-[12px] rounded-[4px] border cursor-pointer transition-all ${
                        formData.discountType === "FIXED_AMOUNT"
                          ? "border-[#4285F4] bg-[#F0F6FF]"
                          : "border-[#d9d9d9] hover:border-[#bbb] bg-white"
                      }`}
                    >
                      <span className={`text-[13px] ${formData.discountType === "FIXED_AMOUNT" ? "text-[#4285F4] font-medium" : "text-[#252525]"}`}>
                        금액 할인
                      </span>
                      {formData.discountType === "FIXED_AMOUNT" && (
                        <div className="w-[18px] h-[18px] rounded-full bg-[#4285F4] flex items-center justify-center">
                          <Check size={11} className="text-white" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* 할인 값 */}
                <div className="flex flex-col gap-[6px]">
                  <label className="text-[14px] text-black text-left">
                    할인 값 *
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={formData.discountValue}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          discountValue: Number(e.target.value),
                        })
                      }
                      className="w-full h-[36px] rounded-[4px] border border-[#d9d9d9] px-[12px] pr-[36px] text-[13px] text-[#252525] text-left focus:border-[#4285F4] focus:outline-none"
                      placeholder={
                        formData.discountType === "PERCENTAGE" ? "10" : "5000"
                      }
                    />
                    <span className="absolute right-[12px] top-1/2 -translate-y-1/2 text-[#555] text-[13px]">
                      {formData.discountType === "PERCENTAGE" ? "%" : "원"}
                    </span>
                  </div>
                  {formData.discountType === "PERCENTAGE" && (
                    <p className="text-[11px] text-[#757575] text-left">
                      1~100 사이의 값을 입력하세요.
                    </p>
                  )}
                </div>

                {/* 이벤트 기간 */}
                <div className="flex flex-col gap-[6px]">
                  <label className="text-[14px] text-black text-left">
                    이벤트 기간 *
                  </label>
                  <div className="flex gap-[8px] items-center">
                    <input
                      type="datetime-local"
                      value={formData.startDate}
                      onChange={(e) =>
                        setFormData({ ...formData, startDate: e.target.value })
                      }
                      className="flex-1 h-[36px] rounded-[4px] border border-[#d9d9d9] px-[12px] text-[13px] text-[#555] text-left focus:border-[#4285F4] focus:outline-none"
                    />
                    <span className="text-[#787A7D] text-[13px]">~</span>
                    <input
                      type="datetime-local"
                      value={formData.endDate}
                      onChange={(e) =>
                        setFormData({ ...formData, endDate: e.target.value })
                      }
                      className="flex-1 h-[36px] rounded-[4px] border border-[#d9d9d9] px-[12px] text-[13px] text-[#555] text-left focus:border-[#4285F4] focus:outline-none"
                    />
                  </div>
                </div>

                {/* 적용 대상 선택 */}
                <div className="flex flex-col gap-[6px]">
                  <label className="text-[14px] text-black text-left">
                    적용 대상
                  </label>
                  <div className="flex flex-col gap-[8px]">
                    <div className="flex gap-[16px]">
                      <label className="flex items-center gap-[6px] cursor-pointer">
                        <input
                          type="radio"
                          name="applyTarget"
                          checked={applyToAll}
                          onChange={() => {
                            setApplyToAll(true);
                            setSelectedProductIds([]);
                          }}
                          className="w-[14px] h-[14px] accent-[#4285F4]"
                        />
                        <span className="text-[13px] text-[#252525]">전체 상품</span>
                      </label>
                      <label className="flex items-center gap-[6px] cursor-pointer">
                        <input
                          type="radio"
                          name="applyTarget"
                          checked={!applyToAll}
                          onChange={() => setApplyToAll(false)}
                          className="w-[14px] h-[14px] accent-[#4285F4]"
                        />
                        <span className="text-[13px] text-[#252525]">특정 상품 선택</span>
                      </label>
                    </div>

                    {!applyToAll && (
                      <div className="border border-[#d9d9d9] rounded-[4px]">
                        {isLoadingProducts ? (
                          <div className="p-[12px] text-center text-[#757575] text-[12px]">
                            상품 목록 로딩 중...
                          </div>
                        ) : products.length === 0 ? (
                          <div className="p-[12px] text-center text-[#757575] text-[12px]">
                            등록된 상품이 없습니다.
                          </div>
                        ) : (
                          <>
                            <div className="px-[12px] py-[8px] bg-[#fafafa] border-b border-[#d9d9d9] flex justify-between items-center">
                              <span className="text-[11px] text-[#555]">
                                {selectedProductIds.length}개 선택됨
                              </span>
                              <button
                                type="button"
                                onClick={() => {
                                  if (selectedProductIds.length === products.length) {
                                    setSelectedProductIds([]);
                                  } else {
                                    setSelectedProductIds(products.map((p) => p.id));
                                  }
                                }}
                                className="text-[11px] text-[#4285F4] hover:underline"
                              >
                                {selectedProductIds.length === products.length
                                  ? "전체 해제"
                                  : "전체 선택"}
                              </button>
                            </div>
                            <div className="max-h-[160px] overflow-y-auto p-[8px]">
                              {products.map((product) => (
                                <label
                                  key={product.id}
                                  className="flex items-center justify-start gap-[12px] py-[6px] px-[8px] hover:bg-[#f5f5f5] rounded-[4px] cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedProductIds.includes(product.id)}
                                    onChange={() => toggleProductSelection(product.id)}
                                    className="w-[14px] h-[14px] accent-[#4285F4] rounded shrink-0"
                                  />
                                  <span className="text-[12px] text-[#252525] text-left">
                                    {product.name}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    {applyToAll && (
                      <div className="bg-[#F0F6FF] rounded-[4px] px-[12px] py-[10px] border border-[#4285F4]/20">
                        <p className="text-[11px] text-[#4285F4] text-left">
                          전체 상품에 할인이 적용됩니다.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 푸터 버튼 */}
              <div className="mt-[24px] pt-[16px] border-t border-[#d9d9d9] flex justify-center gap-[12px]">
                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    resetFormData();
                  }}
                  className="h-[32px] w-[100px] px-[24px] border border-[#d9d9d9] rounded-[4px] text-[14px] text-[#757575] hover:bg-[#f5f5f5] transition-colors"
                >
                  취소
                </button>
                <button
                  onClick={handleSubmit}
                  className="h-[32px] w-[100px] px-[24px] bg-[#4285F4] rounded-[4px] text-[14px] text-white hover:bg-[#3b78e7] transition-colors"
                >
                  {editingDiscount ? "수정" : "추가"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </CompanyAdminLayout>
  );
};

export default EventDiscountManagementPage;
